import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Database, Tag, Printer, ClipboardList, Upload, Download, Search, Save, Plus, Minus, FileText } from "lucide-react";
import { api, API, getToken } from "../../../lib/auth";
import { Card, PageHeader, PrimaryButton, Field, TextInput } from "./_shared";
import JsBarcode from "jsbarcode";

// =================== Opening Stock ===================
export function OpeningStock() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [drafts, setDrafts] = useState({}); // {productId: newQty}

  const load = async () => {
    const r = await api.get("/t/products-stock");
    setRows(r.data);
  };
  useEffect(() => { load(); }, []);

  const adjust = async (pid, change, reason = "Opening stock") => {
    if (!change) return;
    await api.post("/t/stock/adjust", { product_id: pid, change: Number(change), reason });
    toast.success("Stock updated");
    setDrafts({ ...drafts, [pid]: "" });
    load();
  };

  const filtered = rows.filter((r) => !search || r.name?.toLowerCase().includes(search.toLowerCase()) || r.code?.toLowerCase().includes(search.toLowerCase()));
  const totalValue = rows.reduce((s, r) => s + (r.value || 0), 0);

  return (
    <div data-testid="opening-stock-page" className="p-6 lg:p-8">
      <PageHeader title="Opening Stock" breadcrumb="Products · Opening Stock" />

      <Card className="p-4 mb-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 bg-[#FAFBFB] rounded-lg px-3 py-2 flex-1 min-w-[260px]">
          <Search size={16} className="text-black/40" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products…"
            data-testid="os-search" className="flex-1 bg-transparent focus:outline-none text-sm" />
        </div>
        <div className="text-sm">
          <span className="text-black/55">Total stock value: </span>
          <span className="font-display font-extrabold text-[#1FA868] text-lg">₦{Math.round(totalValue).toLocaleString()}</span>
        </div>
      </Card>

      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left p-3.5 font-bold">Product</th>
              <th className="text-left p-3.5 font-bold">Code</th>
              <th className="text-left p-3.5 font-bold">Cost</th>
              <th className="text-left p-3.5 font-bold">Current Stock</th>
              <th className="text-left p-3.5 font-bold">Value</th>
              <th className="text-left p-3.5 font-bold">Adjust</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {filtered.map((p) => (
              <tr key={p.id} className="hover:bg-[#FAFBFB]">
                <td className="p-3.5 font-semibold text-black">{p.name}</td>
                <td className="p-3.5 font-mono text-xs text-black/65">{p.code || p.sku}</td>
                <td className="p-3.5 tabular-nums">₦{Number(p.cost || 0).toLocaleString()}</td>
                <td className="p-3.5 font-bold tabular-nums">{p.stock}</td>
                <td className="p-3.5 tabular-nums text-[#1FA868]">₦{Math.round(p.value).toLocaleString()}</td>
                <td className="p-3.5">
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => adjust(p.id, -1)} data-testid={`os-minus-${p.id}`}
                      className="p-1.5 rounded-md bg-rose-50 text-rose-600 hover:bg-rose-100"><Minus size={12} /></button>
                    <input type="number" value={drafts[p.id] ?? ""} onChange={(e) => setDrafts({ ...drafts, [p.id]: e.target.value })}
                      placeholder="qty" data-testid={`os-input-${p.id}`}
                      className="w-20 px-2 py-1.5 rounded-md border border-black/15 text-sm text-center" />
                    <button onClick={() => adjust(p.id, Number(drafts[p.id] || 0))} data-testid={`os-apply-${p.id}`}
                      className="px-2 py-1.5 rounded-md bg-[#E8F5E9] text-[#1FA868] text-xs font-bold hover:bg-[#D1F0DA]">Apply</button>
                    <button onClick={() => adjust(p.id, 1)} data-testid={`os-plus-${p.id}`}
                      className="p-1.5 rounded-md bg-[#E8F5E9] text-[#1FA868] hover:bg-[#D1F0DA]"><Plus size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-black/45">No products.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// =================== Print Labels ===================
function BarcodeSVG({ value, name, price }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && value) {
      try {
        JsBarcode(ref.current, String(value), {
          format: "CODE128", width: 1.6, height: 40, fontSize: 11, margin: 0, displayValue: true,
        });
      } catch {}
    }
  }, [value]);
  return (
    <div className="border-2 border-dashed border-black/15 rounded-lg p-3 text-center bg-white">
      <div className="font-bold text-xs text-black truncate">{name}</div>
      <div className="text-[10px] text-black/55 tabular-nums">₦{Number(price).toLocaleString()}</div>
      <svg ref={ref} className="mx-auto mt-1" />
    </div>
  );
}

export function PrintLabels() {
  const [products, setProducts] = useState([]);
  const [picks, setPicks] = useState({}); // {productId: qty}
  const [search, setSearch] = useState("");
  const [labels, setLabels] = useState([]);

  useEffect(() => { api.get("/t/products").then((r) => setProducts(r.data)); }, []);

  const generate = async () => {
    const items = Object.entries(picks).filter(([, q]) => Number(q) > 0).map(([product_id, qty]) => ({ product_id, qty: Number(qty) }));
    if (!items.length) return toast.error("Pick at least one product & quantity");
    const r = await api.post("/t/products/labels", { items });
    setLabels(r.data.labels);
    toast.success(`Generated ${r.data.count} labels`);
  };

  const filtered = products.filter((p) => !search || p.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="print-labels-page" className="p-6 lg:p-8">
      <PageHeader title="Print Labels" breadcrumb="Products · Print Labels"
        actions={<>
          <PrimaryButton onClick={() => window.print()} icon={Printer} testid="pl-print">Print</PrimaryButton>
        </>} />

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-5">
          <h3 className="font-display font-bold text-base text-black mb-3">Pick products</h3>
          <div className="flex items-center gap-2 bg-[#FAFBFB] rounded-lg px-3 py-2 mb-3">
            <Search size={16} className="text-black/40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" data-testid="pl-search"
              className="flex-1 bg-transparent focus:outline-none text-sm" />
          </div>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {filtered.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 p-2 rounded-lg hover:bg-[#FAFBFB]">
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-black truncate">{p.name}</div>
                  <div className="text-[11px] font-mono text-black/45">{p.code || p.sku} · ₦{Number(p.price).toLocaleString()}</div>
                </div>
                <input type="number" min="0" value={picks[p.id] || ""} onChange={(e) => setPicks({ ...picks, [p.id]: e.target.value })}
                  placeholder="qty" data-testid={`pl-qty-${p.id}`}
                  className="w-20 px-2 py-1.5 rounded-md border border-black/15 text-sm text-center" />
              </div>
            ))}
          </div>
          <PrimaryButton onClick={generate} icon={Tag} testid="pl-generate">Generate Labels</PrimaryButton>
        </Card>

        <Card className="p-5">
          <h3 className="font-display font-bold text-base text-black mb-3">Preview ({labels.length} labels)</h3>
          {labels.length === 0 ? (
            <div className="text-sm text-black/45 py-12 text-center border-2 border-dashed border-black/10 rounded-xl">
              <Tag size={32} className="mx-auto mb-2 opacity-40" />
              Pick products and click "Generate Labels" to preview.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[60vh] overflow-y-auto print:grid-cols-4">
              {labels.map((l, i) => <BarcodeSVG key={i} value={l.code} name={l.name} price={l.price} />)}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// =================== Count Stock ===================
export function CountStock() {
  const [products, setProducts] = useState([]);
  const [counts, setCounts] = useState({});
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/t/products-stock").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const rows = Object.entries(counts).filter(([, v]) => v !== "" && v !== undefined && v !== null)
      .map(([product_id, counted]) => ({ product_id, counted: Number(counted) }));
    if (!rows.length) return toast.error("Enter at least one count");
    setBusy(true);
    try {
      const r = await api.post("/t/stock/count", rows);
      toast.success(`Updated ${r.data.results.length} products`);
      setCounts({});
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const filtered = products.filter((p) => !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.code?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="count-stock-page" className="p-6 lg:p-8">
      <PageHeader title="Count Stock" breadcrumb="Products · Count Stock"
        actions={<PrimaryButton onClick={submit} disabled={busy} icon={Save} testid="cs-submit">{busy ? "Saving…" : "Save Counts"}</PrimaryButton>} />

      <Card className="p-3 mb-4">
        <div className="flex items-center gap-2 bg-[#FAFBFB] rounded-lg px-3 py-2">
          <Search size={16} className="text-black/40" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products to count…" data-testid="cs-search"
            className="flex-1 bg-transparent focus:outline-none text-sm" />
        </div>
      </Card>

      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left p-3.5 font-bold">Product</th>
              <th className="text-left p-3.5 font-bold">Code</th>
              <th className="text-left p-3.5 font-bold">System</th>
              <th className="text-left p-3.5 font-bold">Counted</th>
              <th className="text-left p-3.5 font-bold">Diff</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {filtered.map((p) => {
              const c = counts[p.id];
              const diff = c === "" || c === undefined ? null : Number(c) - p.stock;
              return (
                <tr key={p.id} className="hover:bg-[#FAFBFB]">
                  <td className="p-3.5 font-semibold text-black">{p.name}</td>
                  <td className="p-3.5 font-mono text-xs text-black/65">{p.code || p.sku}</td>
                  <td className="p-3.5 tabular-nums font-bold">{p.stock}</td>
                  <td className="p-3.5">
                    <input type="number" value={counts[p.id] ?? ""} onChange={(e) => setCounts({ ...counts, [p.id]: e.target.value })}
                      placeholder="0" data-testid={`cs-input-${p.id}`}
                      className="w-24 px-2 py-1.5 rounded-md border border-black/15 text-sm text-center" />
                  </td>
                  <td className="p-3.5 tabular-nums">
                    {diff === null ? "—" : (
                      <span className={diff === 0 ? "text-black/55" : diff > 0 ? "text-[#1FA868] font-bold" : "text-rose-600 font-bold"}>
                        {diff > 0 ? "+" : ""}{diff}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// =================== Import Products ===================
export function ImportProducts({ updateOnly = false }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!file) return toast.error("Choose a CSV file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("update_only", updateOnly ? "true" : "false");
      const r = await fetch(`${API}/t/products/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const j = await r.json();
      setResult(j);
      toast.success(`Created ${j.created}, updated ${j.updated}, skipped ${j.skipped}`);
    } catch (e) { toast.error("Import failed"); }
    finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const csv = "name,code,cost,price,stock,stock_alert,active\nWidget,WID-001,100,150,50,10,true\nGadget,GAD-002,200,350,20,5,true";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "products_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const title = updateOnly ? "Import (Update Only)" : "Import products";
  const breadcrumb = `Products · ${title}`;

  return (
    <div data-testid={updateOnly ? "import-update-page" : "import-page"} className="p-6 lg:p-8 max-w-3xl">
      <PageHeader title={title} breadcrumb={breadcrumb}
        actions={<button onClick={downloadTemplate} data-testid="import-template"
          className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl bg-white border border-black/10 text-sm font-semibold hover:border-black/25">
          <Download size={13} /> Download Template
        </button>} />

      <Card className="p-7">
        <div className="rounded-xl border-2 border-dashed border-black/15 p-10 text-center hover:border-[#1FA868] hover:bg-[#F0FAF3] transition-colors">
          <Upload size={36} className="mx-auto text-black/30" />
          <div className="mt-3 font-semibold text-black">{file ? file.name : "Drop CSV or click to browse"}</div>
          <div className="text-[11px] text-black/45 mt-1">Required columns: name, code, cost, price, stock, stock_alert, active</div>
          <input type="file" accept=".csv,text/csv" data-testid="import-file"
            onChange={(e) => { setFile(e.target.files?.[0]); setResult(null); }}
            className="mt-4 mx-auto text-sm" />
        </div>

        {updateOnly && (
          <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3">
            ⚠ Update-only mode: rows with unknown <code className="font-mono">code</code> will be skipped.
          </div>
        )}

        <div className="mt-5">
          <PrimaryButton onClick={submit} disabled={busy || !file} icon={Upload} testid="import-submit">
            {busy ? "Importing…" : "Start Import"}
          </PrimaryButton>
        </div>

        {result && (
          <div className="mt-5 grid grid-cols-3 gap-3">
            <div className="rounded-xl bg-[#E8F5E9] p-4 text-center">
              <div className="text-[10px] uppercase tracking-wider text-[#1FA868] font-bold">Created</div>
              <div className="font-display font-extrabold text-2xl text-[#1FA868]">{result.created}</div>
            </div>
            <div className="rounded-xl bg-blue-50 p-4 text-center">
              <div className="text-[10px] uppercase tracking-wider text-blue-700 font-bold">Updated</div>
              <div className="font-display font-extrabold text-2xl text-blue-700">{result.updated}</div>
            </div>
            <div className="rounded-xl bg-amber-50 p-4 text-center">
              <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Skipped</div>
              <div className="font-display font-extrabold text-2xl text-amber-700">{result.skipped}</div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

export const ImportUpdate = () => <ImportProducts updateOnly={true} />;
